'use client';

import { useState } from 'react';

import { doc, firestore, updateDoc } from '@ps/functions/firebase/firestore';
import { Checkbox } from '@ps/ui/atoms/checkbox';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { useCurrentUserPrivateProfile } from '@ps/ui/contexts/user-profile';

/**
 * 18+ self-certification. Configuring any paid subscription tier is gated on it
 * (see `useTierItems`, which disables the paid rows until `isAgeCertified`).
 * Renders nothing once certified. Port of PopshopRN's `AgeVerificationCheckBox`
 * from the subscription-configuration page header.
 *
 * Note: the legacy component also seeded `profiles.followersSettings` on first
 * certify. We skip that here — `useTierItems` already treats an absent
 * `followersSettings` as an active FOLLOWER tier, so no seed write is needed.
 */
export function AgeVerificationCheckbox({ className }: { className?: string }) {
  const userId = useCurrentUserId();
  const privateProfile = useCurrentUserPrivateProfile();
  const [saving, setSaving] = useState(false);

  if (privateProfile?.isAgeCertified) return null;

  const onCertify = (checked: boolean) => {
    if (!userId || !checked) return;
    setSaving(true);
    void (async () => {
      try {
        await updateDoc(doc(firestore(), 'privateProfiles', userId), {
          isAgeCertified: true,
        });
      } catch (error) {
        console.error('subscription: age certification failed:', error);
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <label
      className={`text-content-1 flex items-center gap-2 text-sm ${className ?? ''}`}
    >
      <Checkbox
        checked={false}
        disabled={saving}
        onCheckedChange={value => onCertify(value === true)}
        className="data-[state=checked]:!border-brand data-[state=checked]:!bg-brand data-[state=checked]:!text-gray-900"
      />
      <span className="font-light">
        I certify that I am 18 years of age or older
      </span>
    </label>
  );
}
